import React, { useState } from "react";
import { useTheme } from "../BrandColors/Context/ThemeContext";

const DynamicForm = () => {
  const [fields, setFields] = useState([]);
  const [introAdded, setIntroAdded] = useState(false);
  const [formData, setFormData] = useState({});
  const [stepperData, setStepperData] = useState([]);
  const [prompt, setPrompt] = useState("");
  const theme = useTheme() || "#3b82f6";

  const handleAddField = (type) => {
    const id = Date.now();
    if (type === "intro" && introAdded) {
      alert("You can only add one Intro!");
      return;
    }
    setFields([...fields, { id, type }]);
    if (type === "intro") {
      setIntroAdded(true);
    }
  };

  const handleRemoveField = (id) => {
    const updatedFields = fields.filter((field) => field.id !== id);
    setFields(updatedFields);

    const updatedFormData = { ...formData };
    delete updatedFormData[id];
    setFormData(updatedFormData);

    const removedField = fields.find((field) => field.id === id);
    if (removedField?.type === "intro") {
      setIntroAdded(false);
    }
  };

  const handleFieldChange = (id, fieldName, value) => {
    setFormData({
      ...formData,
      [id]: {
        ...formData[id],
        [fieldName]: value,
      },
    });
  };

  const handleStepperNameChange = (stepIndex, value) => {
    const updatedStepperData = [...stepperData];
    updatedStepperData[stepIndex] = { stepName: value };
    setStepperData(updatedStepperData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const result = fields.map((field) => {
      const data = formData[field.id] || {};
      if (field.type === "intro") {
        return { intro: data.intro || "" };
      } else if (field.type === "script") {
        return {
          scriptName: data.scriptName || "",
          script: data.script || "",
        };
      }
      return {};
    });

    result.splice(1, 0, { prompt });

    result.push({ stepperData });

    console.log("Submitted Data:", result);
    alert(JSON.stringify(result, null, 2));

    setFields([]);
    setIntroAdded(false);
    setFormData({});
    setStepperData([]);
    setPrompt("");
  };

  const totalFields = fields.length;

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6 animate-fade-in-up">
      <div className="glass-panel w-full max-w-4xl p-8 rounded-2xl shadow-2xl border border-blue-200/50 relative overflow-hidden">
        
        {/* Decorative background element */}
        <div 
          className="absolute top-0 left-0 w-full h-24 opacity-20 -z-10"
          style={{ background: `linear-gradient(to bottom, ${theme}, transparent)` }}
        />

        <div className="mb-8 border-b border-blue-200/50 pb-4">
          <h1 className="text-3xl font-bold text-white tracking-tight">API Creator</h1>
          <p className="text-sm text-slate-600 mt-2">Configure dynamic fields and scripts for the agent.</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-8">
          
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-slate-700">Add New Component</label>
            <select
              className="input-field max-w-xs"
              onChange={(e) => {
                if (e.target.value) {
                  handleAddField(e.target.value);
                  e.target.value = "";
                }
              }}
            >
              <option value="" className="bg-blue-50">Select Field to Add</option>
              {!introAdded && <option value="intro" className="bg-blue-50">Add Intro</option>}
              <option value="script" className="bg-blue-50">Add Script</option>
            </select>
          </div>

          {fields.length > 0 && (
            <div className="flex flex-col gap-6">
              <h2 className="text-lg font-semibold text-white border-b border-blue-200/50 pb-2">Components</h2>
              {fields.map((field, index) => (
                <div key={field.id} className="bg-white/50 p-6 rounded-xl border border-blue-200/50 relative group">
                  <div className="absolute -left-3 -top-3 w-8 h-8 rounded-full bg-blue-100 border border-slate-600 flex items-center justify-center text-xs font-bold text-slate-700 shadow-lg">
                    {index + 1}
                  </div>
                  
                  {field.type === "intro" && (
                    <div className="flex flex-col gap-1.5 w-full">
                      <label htmlFor={`intro-${field.id}`} className="text-sm font-medium text-slate-700">
                        Intro Text
                      </label>
                      <textarea
                        id={`intro-${field.id}`}
                        className="input-field min-h-[100px] resize-y"
                        placeholder="Enter Intro"
                        value={formData[field.id]?.intro || ""}
                        onChange={(e) =>
                          handleFieldChange(field.id, "intro", e.target.value)
                        }
                      />
                    </div>
                  )}
                  
                  {field.type === "script" && (
                    <div className="flex flex-col gap-5 w-full">
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor={`scriptName-${field.id}`} className="text-sm font-medium text-slate-700">
                          Script Name
                        </label>
                        <input
                          type="text"
                          id={`scriptName-${field.id}`}
                          className="input-field"
                          placeholder="Enter Script Name"
                          value={formData[field.id]?.scriptName || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              field.id,
                              "scriptName",
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor={`script-${field.id}`} className="text-sm font-medium text-slate-700">
                          Script Content
                        </label>
                        <textarea
                          id={`script-${field.id}`}
                          className="input-field min-h-[120px] resize-y"
                          placeholder="Enter Script"
                          value={formData[field.id]?.script || ""}
                          onChange={(e) =>
                            handleFieldChange(field.id, "script", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    className="absolute top-4 right-4 px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 bg-red-400/10 hover:bg-red-400/20 hover:text-red-300 transition-colors border border-red-400/20"
                    onClick={() => handleRemoveField(field.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white/50 p-6 rounded-xl border border-blue-200/50 flex flex-col gap-1.5">
            <h2 className="text-lg font-semibold text-white mb-2 border-b border-blue-200/50 pb-2">Global Settings</h2>
            <label className="text-sm font-medium text-slate-700">System Prompt</label>
            <textarea
              className="input-field min-h-[120px] font-mono text-sm resize-y"
              placeholder="Enter your prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </div>

          {totalFields > 0 && (
            <div className="bg-white/50 p-6 rounded-xl border border-blue-200/50">
              <h2 className="text-lg font-semibold text-white mb-4 border-b border-blue-200/50 pb-2">Stepper Configuration</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: totalFields }).map((_, index) => {
                  const stepNumber = index + 1;
                  return (
                    <div key={stepNumber} className="flex flex-col gap-1.5">
                      <label htmlFor={`stepName-${stepNumber}`} className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                        Step {stepNumber} Name
                      </label>
                      <input
                        type="text"
                        id={`stepName-${stepNumber}`}
                        className="input-field"
                        placeholder={`Enter Step ${stepNumber} Name`}
                        value={stepperData[stepNumber - 1]?.stepName || ""}
                        onChange={(e) =>
                          handleStepperNameChange(stepNumber - 1, e.target.value)
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-blue-200/50 flex justify-end">
            <button 
              type="submit" 
              className="px-8 py-3 rounded-lg font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5 active:translate-y-0"
              style={{ backgroundColor: theme }}
            >
              Generate API Configuration
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DynamicForm;
